import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

export default function SaveModal({ open, onClose, inspectionId, state }) {
  const navigate = useNavigate();
  const [finishing, setFinishing] = useState(false);
  const [types, setTypes] = useState([]);

  useEffect(() => {
    if (!open) return;

    api.get('/inspection-types')
      .then((res) => {
        setTypes((res.data || []).filter((type) => type.is_active));
      })
      .catch(() => setTypes([]));
  }, [open]);

  const sections = useMemo(() => {
    const activeTypes = types.length ? types : [
      { key: 'steering', done_field: 'steering_done', name: 'فحص التوجيه والدفع', route_path: 'steering' },
      { key: 'engine', done_field: 'engine_done', name: 'فحص الماكينة', route_path: 'engine' },
      { key: 'computer', done_field: 'computer_done', name: 'فحص الكمبيوتر', route_path: 'computer' },
      { key: 'body', done_field: 'body_done', name: 'فحص البودي', route_path: 'body' },
    ];

    return activeTypes.map((type) => ({
      id: type.id || type.key,
      key: type.done_field,
      sectionKey: type.key,
      title: `${type.icon ? `${type.icon} ` : ''}${type.name}`,
      path: type.route_path,
    }));
  }, [types]);

  const resultsBySection = (state?.inspection_results || []).reduce((acc, result) => {
    const section = result?.inspection_item?.inspection_type?.key || result?.inspection_item?.section;
    if (section) acc[section] = true;
    return acc;
  }, {});

  const resolveDone = (section) => {
    if (section.key && Object.prototype.hasOwnProperty.call(state || {}, section.key)) {
      return Boolean(state?.[section.key]);
    }

    if (section.sectionKey === 'body') return Boolean((state?.body_results || []).length);
    if (section.sectionKey === 'computer') return Boolean(state?.computer_result);
    return Boolean(resultsBySection[section.sectionKey]);
  };

  const allDone = sections.length > 0 && sections.every((s) => resolveDone(s));

  if (!open) return null;

  const finishNow = async () => {
    if (finishing) return;

    const confirmMessage = allDone
      ? 'هل تريد إنهاء الفحص الآن؟'
      : 'بعض الأقسام غير مكتملة. هل تريد إنهاء الفحص الآن على أي حال؟';

    if (!window.confirm(confirmMessage)) return;

    setFinishing(true);
    try {
      await api.patch(`/inspections/${inspectionId}/complete`, {});
      navigate(`/inspection/${inspectionId}/report`);
    } catch {
      alert('تعذر إنهاء الفحص الآن. حاول مرة أخرى.');
    } finally {
      setFinishing(false);
    }
  };

  const continueLater = () => {
    onClose?.();
    navigate(`/inspection/${inspectionId}/menu`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="text-xl font-bold text-green-600 mb-4">✅ تم الحفظ بنجاح</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sections.map((section) => (
            <button
              key={section.id || section.key || section.sectionKey}
              disabled={!section.path}
              className={`rounded-xl border p-3 text-right transition ${
                resolveDone(section)
                  ? 'bg-green-100 border-green-400 text-green-800'
                  : 'bg-gray-50 border-gray-300 text-gray-700 disabled:opacity-60'
              }`}
              onClick={() => section.path && navigate(`/inspection/${inspectionId}/${section.path}`)}
            >
              {resolveDone(section) ? '✔ ' : ''}
              {section.title}
            </button>
          ))}
        </div>

        <div className="mt-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={continueLater}
              className="rounded-xl border border-primary text-primary px-4 py-2"
            >
              💾 حفظ ومتابعة لاحقًا
            </button>

            <button
              onClick={finishNow}
              disabled={finishing}
              className="rounded-xl bg-primary px-4 py-2 text-white disabled:opacity-60"
            >
              {finishing ? 'جاري الإنهاء...' : '🏁 إنهاء الفحص'}
            </button>
            {!allDone && (
              <span className="text-sm text-amber-600">يمكن الإنهاء الآن حتى مع وجود أقسام غير مكتملة</span>
            )}
          </div>

          <button onClick={onClose} className="rounded-xl border px-4 py-2">
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
}
